'use client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { reviewQueueSchema, type Product } from '@curio/contracts';
import { api, useAccount, normalize } from '@curio/client';
import { Loading } from '@curio/ui';
import { SignInPrompt } from '../components/products';
import { Browse } from './browse';
import { Preferences } from './preferences';
import { ProfileHeader, type ProfileTab } from './profile-header';
import { ProjectList } from './project-list';
import { ReviewQueuePanel } from './review-queue-panel';

/** The signed-in home: profile header with tabs, then contribution, projects, and review rows. */
export function Dashboard({ tab }: { tab: ProfileTab }) {
  const account = useAccount();
  const client = useQueryClient();
  const projects = useQuery({
    queryKey: ['projects', account.data?.id],
    enabled: !!account.data,
    queryFn: async () => {
      const data = await api<{ products: Product[] }>('/me/projects');
      normalize(client, data.products);
      return data;
    },
  });
  const queue = useQuery({
    queryKey: ['review-queue', account.data?.id],
    enabled: !!account.data && tab === 'overview',
    queryFn: async () => {
      const data = await api('/review-queue', {}, reviewQueueSchema);
      normalize(client, data.products);
      return data;
    },
  });
  if (account.isPending) return <Loading />;
  if (!account.data) return <SignInPrompt />;
  const a = account.data;
  return (
    <div className="page profile-page">
      <ProfileHeader
        account={a}
        tab={tab}
        projectCount={projects.data?.products.length}
      />
      {tab === 'overview' ? (
        <div className="profile-rows">
          <ProjectList
            account={a}
            projects={projects.data?.products ?? []}
            pending={projects.isPending}
          />
          <ReviewQueuePanel account={a} queue={queue} />
        </div>
      ) : tab === 'saved' ? (
        <Browse saved embedded />
      ) : (
        <Preferences account={a} />
      )}
    </div>
  );
}
